import React, {Component} from 'react';
import compose from './utils/compose';
import PropTypes from 'prop-types';

// Layout helpers
import debounce from './utils/debounce';

const DB_TIME = 350;

export const withMobileLayoutHandler =
  ({additionalClassName = '', defaultHeight = '100vh', offsetHeight, updateMinHeight = true} = {}) =>
  (ChildComponent) => {
    class WithMobileLayoutHandler extends Component {
      constructor(props) {
        super(props);
        const derivedOffsetHeight = offsetHeight || props.offsetHeight;
        this.state = {
          height: derivedOffsetHeight ? `calc(${defaultHeight} - ${derivedOffsetHeight})` : defaultHeight
        };
      }

      isIOSDevice = () => {
        return window.navigator && !!window.navigator.platform && /iPad|iPhone|iPod/i.test(window.navigator.platform);
      };

      componentDidMount() {
        this.resetLayoutPosition();
        if ('undefined' !== typeof window) {
          window.addEventListener('resize', this.handleSizeChange);
        }
      }

      componentDidUpdate(prevProps) {
        const {location, offsetHeight} = this.props;
        if (location.pathname !== prevProps.location.pathname) {
          this.resetLayoutPosition();
        }
        if (offsetHeight !== prevProps.offsetHeight) {
          this.handleSizeChange();
        }
      }

      componentWillUnmount() {
        this.handleSizeChange.cancel();
        if ('undefined' !== typeof window) {
          window.removeEventListener('resize', this.handleSizeChange);
        }
      }

      /**
       * Measure initial screen height, and scroll to top (on mount and changing screens)
       */
      resetLayoutPosition = () => {
        if ('undefined' === typeof window) {
          return;
        }
        window.scrollTo(0, 0);
        if (this.isIOSDevice()) {
          this.handleSizeChange();
        }
      };

      /**
       * This function calculates real innerHeight for iOS devices with notch
       * @return {string | number}
       */
      getInnerHeight = () => {
        let height = defaultHeight;
        if ('undefined' !== typeof window) {
          height = window.innerHeight;
        }

        return `${height}px`;
      };

      /**
       * Debounced screen size change function for onResize listener, good for Safari (because it fires to much events
       * on screen rotation);
       * @type {function}
       */
      handleSizeChange = debounce(() => {
        const height = this.getInnerHeight();
        const {offsetHeight} = this.props;
        const derivedOffsetHeight = offsetHeight || offsetHeight;
        this.setState({
          height: derivedOffsetHeight ? `calc(${height} - ${derivedOffsetHeight})` : height
        });
      }, DB_TIME);

      render() {
        const {height} = this.state;

        return (
          <div
            className={additionalClassName}
            style={{
              height,
              ...(updateMinHeight && {minHeight: height})
            }}
          >
            <ChildComponent {...this.props} />
          </div>
        );
      }
    }

    WithMobileLayoutHandler.propTypes = {
      location: PropTypes.object,
      offsetHeight: PropTypes.number
    };

    return WithMobileLayoutHandler;
  };

export default (options) => (ChildComponent) => compose(withMobileLayoutHandler(options), ChildComponent);
